import React, { useCallback, useState } from 'react'
import { F0Button, F0Text } from '@factorialco/f0-react'
import { F0Callout, Stack } from '@factorialco/f0-react/dist/experimental'
import { Download } from '@factorialco/f0-react/icons/app'
import { useDirectUpload } from 'modules/core/hooks/useDirectUpload'
import { useMutation } from 'modules/core/hooks/useMutation'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { AddTicketAttachmentDocument, type TicketDetailQuery } from 'generated/resources/graphql'
import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import DocumentType from 'modules/core/components/DocumentType'

type Ticket = NonNullable<TicketDetailQuery['ticketing']['ticket']>

type AttachmentNode = Ticket['savedAttachmentsConnection']['edges'][number]['node']

export type TicketAttachmentsProps = {
  ticket: Ticket
  onAttachmentAdded: () => void
}

// Mirror Ticketing::Constants::TicketTypes::ATTACHMENT_MAX_BYTES
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024

const getAcceptedFileTypes = () =>
  acceptedContentTypes(['application/pdf', 'image/jpeg', 'image/png'])

const AttachmentItem: React.FC<{ attachment: AttachmentNode }> = ({ attachment }) => {
  const handleDownload = useCallback(() => {
    window.open(attachment.url, '_blank', 'noopener,noreferrer')
  }, [attachment.url])

  return (
    <Stack
      alignItems="center"
      gap="2"
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid var(--f0-color-border-default)',
        background: 'var(--f0-color-surface-default)',
      }}
    >
      <Stack alignItems="center" gap="2" style={{ flexDirection: 'row', minWidth: 0, flex: 1 }}>
        <Stack style={{ flexShrink: 0 }}>
          <DocumentType mimetype={attachment.contentType} />
        </Stack>
        <F0Text variant="small" ellipsis content={attachment.filename} />
      </Stack>
      <Stack style={{ flexShrink: 0 }}>
        <F0Button variant="outline" hideLabel icon={Download} onClick={handleDownload} />
      </Stack>
    </Stack>
  )
}

export const TicketAttachments: React.FC<TicketAttachmentsProps> = ({
  ticket,
  onAttachmentAdded,
}) => {
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const acceptedFileTypes = React.useMemo(() => getAcceptedFileTypes(), [])

  const { upload, status: uploadStatus } = useDirectUpload({
    resourceType: 'Ticketing::Ticket',
    resourceId: String(ticket.id),
  })

  const [performAddAttachment] = useMutation(AddTicketAttachmentDocument, {
    extractorFn: (data) => data.ticketingMutations.createTicketingTicketAttachment,
  })

  const isUploading = uploadStatus === 'uploading' || uploadStatus === 'processing'

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0 || !ticket) return
      setAttachmentError(null)

      for (const file of acceptedFiles) {
        if (file.size > MAX_ATTACHMENT_BYTES) {
          setAttachmentError(i18n.t('ticketing.inbox.attachments.file_too_large'))
          return
        }

        try {
          const result = await upload(file)
          if (result.type === 'aborted') return
          const addResult = await performAddAttachment({
            variables: {
              ticketId: ticket.id,
              signedBlobId: result.signedId,
            },
          })
          const handled = handleResult(addResult, () => {})
          if (handled.success) {
            onAttachmentAdded()
          } else {
            const msg = handled.errors
              .map((e) =>
                e.__typename === 'StructuredError'
                  ? e.messages.join(' ')
                  : e.__typename === 'SimpleError'
                    ? e.message
                    : '',
              )
              .filter(Boolean)
              .join(' ')
            setAttachmentError(msg || i18n.t('ticketing.inbox.attachments.add_failed'))
            return
          }
        } catch (err) {
          setAttachmentError(
            err instanceof Error
              ? err.message
              : i18n.t('ticketing.inbox.attachments.upload_failed'),
          )
          return
        }
      }
    },
    [ticket, upload, performAddAttachment, onAttachmentAdded],
  )

  const attachments = ticket.savedAttachmentsConnection.edges

  return (
    <Stack gap="4" paddingY="p-4">
      <F0Text variant="label" content={i18n.t('ticketing.inbox.attachments.section_title')} />

      {attachments.length > 0 && (
        <Stack gap="2">
          {attachments.map(({ node }) => (
            <AttachmentItem key={node.id} attachment={node} />
          ))}
        </Stack>
      )}

      <Stack gap="2">
        <DropzoneBox
          onDrop={handleDrop}
          message={i18n.t('ticketing.inbox.attachments.drop_here')}
          accept={acceptedFileTypes}
          iconName="file-upload"
          multiple
          disabled={isUploading}
        />
        {attachmentError != null && (
          <F0Callout variant="critical" title={i18n.t('common.error')}>
            {attachmentError}
          </F0Callout>
        )}
      </Stack>
    </Stack>
  )
}
